import React, { SyntheticEvent, useCallback, useEffect, useMemo, useState } from "react";

import { Button, DropdownProps, Icon, TextAreaProps } from "semantic-ui-react";

import { v4 as uuidv4 } from "uuid";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import SearchBoxWithKey from "react-lib/appcon/common/SearchBoxWithKey";
import SearchBoxDropdown, { SelectedItemHandler } from "react-lib/appcon/common/SearchBoxDropdown";

import ModalEditImage from "react-lib/apps/common/ModalEditImage";
import ModalUploadFile from "react-lib/apps/common/ModalUploadFile";
import ModInfo from "react-lib/apps/common/ModInfo";
import SnackMessage from "react-lib/apps/common/SnackMessage";
import WebcamComponent from "react-lib/apps/common/WebcamComponent";

import TableTreatmentPlan from "react-lib/apps/HISV3/RHB/TableTreatmentPlan";

import CardRehabExaminationUX from "./CardRehabExaminationUX";

import {
  formatRehabImage,
  getFilteredDivisions,
  RehabImageType,
  RehabPlanItemDetailType,
  RehabPlanType,
} from "./RHBInterface";

type CardRehabExaminationProps = {
  onEvent: (e: any) => any;
  setProp: (key: string, value: any, callback?: () => void) => any;
  controller: any;
  django: any;
  currentDoctor: any;
  rehabExamPlan?: RehabPlanType;
  buttonLoadCheck: any;
  errorMessage: any;
  languageUX: string;
  masterOptions: any;
  searchedItemListWithKey: any;
  selectedEncounter: any;
};

type FormErrorsType = {
  department?: boolean;
  order?: boolean;
  primary?: boolean;
  totals?: number[];
};

const IMAGES = {
  female_diagram: "/static/images/rehab/female-diagram.png",
  male_diagram: "/static/images/rehab/male-diagram.png",
};

const BUTTON_ACTIONS = {
  save: "SAVE",
};

const CARD_REHAB_EXAMINATION = "CardRehabExamination";

const ACTION_SAVE = `${CARD_REHAB_EXAMINATION}_${BUTTON_ACTIONS.save}`;

const CardRehabExamination = (props: CardRehabExaminationProps) => {
  // const [problem, setProblem] = useState("");
  // const [goal, setGoal] = useState("");
  // const [precaution, setPrecaution] = useState<any[]>([]);
  const [order, setOrder] = useState<Record<string, any> | null>(null);
  const [department, setDepartment] = useState<number | null>(null);
  const [tableData, setTableData] = useState<Partial<RehabPlanItemDetailType>[]>([]);
  const [rhbPrecautionOptions, setRhbPrecautionOptions] = useState<any[]>([]);
  const [formErrors, setFormErrors] = useState<FormErrorsType | null>(null);
  const [selectedDoctor, setSelectedDoctor] = useState<Record<string, any> | null>(null);

  const [modalEditImage, setModalEditImage] = useState<number | null>(null);
  const [openModalUploadFile, setOpenModalUploadFile] = useState(false);
  const [openWebCam, setOpenWebCam] = useState(false);

  const [activePanes, setActivePanes] = useState<Record<string, any>[]>([]);
  const [imageOverlay, setImageOverlay] = useState<Record<string, any>[]>([]);
  const [activeIndex, setActiveIndex] = useState<number>(0);

  const isDoctor = useMemo(() => {
    return props.django?.user?.role_types?.find((role: string) => ["DOCTOR"].includes(role));
  }, [props.django?.user]);

  useEffect(() => {
    props.onEvent({
      message: "GetMasterData",
      params: {
        masters: [
          ["rhb_precaution", {}],
          ["RehabilitationList", {}],
        ],
      },
    });
  }, []);

  useEffect(() => {
    const precautionOptions = (props.rehabExamPlan?.precaution ?? []).map((value) => ({
      key: value,
      text: value,
      value,
    }));
    const options = props.masterOptions?.rhb_precaution || [];

    setRhbPrecautionOptions([
      ...new Map(
        [...options, ...precautionOptions].map((option) => [option.value, option])
      ).values(),
    ]);
  }, [props.masterOptions?.rhb_precaution, props.rehabExamPlan?.precaution]);

  useEffect(() => {
    if (isDoctor) {
      setSelectedDoctor(null);
    } else {
      if (props.rehabExamPlan?.id) {
        setSelectedDoctor(props.rehabExamPlan?.order_by);        
      } else {
        setSelectedDoctor({
          id: props.selectedEncounter?.doctor, 
          name_code: props.selectedEncounter?.doctor_name_code
        });
      }      
    }
  }, [isDoctor, props.rehabExamPlan]);

  useEffect(() => {
    props.onEvent({
      message: "getRhbExamPlan",
      params: {},
    });

    setFormErrors(null);

    return () => {
      props.onEvent({
        message: "clearRhbExamPlan",
        params: {},
      });
    };
  }, [props.selectedEncounter?.id]);

  useEffect(() => {
    const rehabilitationList: any[] | undefined =
      props.controller.data?.masterData?.RehabilitationList;

    console.log("CardRehabExamination rehabilitationList:", rehabilitationList);
    console.log("CardRehabExamination props.rehabExamPlan?.items:", props.rehabExamPlan?.items);

    let items = props.rehabExamPlan?.items ?? [];

    if (items.length > 0 && rehabilitationList) {
      // add treatmentName from product
      items = items.map((item) => {
        const foundRehab = rehabilitationList.find(
          (rehab: any) => item.rehabilitation === rehab.id
        );

        return {
          ...item,
          divisions: foundRehab?.divisions || [],
          productName: foundRehab?.name || "",
        };
      });
    }

    console.log("CardRehabExamination items:", items);
    setTableData(items);
  }, [props.controller.data?.masterData?.RehabilitationList, props.rehabExamPlan?.items]);

  // console.log("CardRehabExamination props: ", props)

  // GENERAL = 1, 'ทั่วไป'
  // BILLING = 2, 'การเงิน'
  // DRUG = 3, 'ห้องยา'
  // SUPPLY = 4, 'ห้องเวชภัณฑ์'
  // SPECIMEN_COLLECTOR = 6, 'แผนกเก็บ Specimen'
  // CLINIC = 7, 'หน่วยตรวจ'
  // PREMIUM_CLINIC = 8, 'คลินิกพรีเมี่ยม'
  // WARD = 9, 'หอผู้ป่วย'
  // ZONE = 10, 'หน่วยย่อย'
  // SHORTSTAY = 11, 'short stay'
  // WARD_PREMIUM = 12, 'หอผู้ป่วยพรีเมี่ยม'
  // OPERATING_ROOM = 16, 'ห้องผ่าตัด'
  // BACK_OFFICE = 17, 'backoffice'

  // useEffect(() => {
  //   console.log(
  //     "rehabDivs props?.controller?.data?.allDivisionList:",
  //     props.controller?.data?.allDivisionList
  //   );

  //   const allDivisionList: any[] = props.controller?.data?.allDivisionList || [];

  //   const rehabDivs = allDivisionList
  //     .filter((item) => item.is_active && [12, 7, 9].includes(item.type))
  //     .map((item) => ({ key: item.id, text: item.name, value: item.id }));

  //   console.log("rehabDivs:", rehabDivs);
  //   setRehabDivisions(rehabDivs);
  // }, [props.controller?.data?.allDivisionList]);
  const rehabDivisions = useMemo(
    () => getFilteredDivisions(props.controller?.data?.allDivisionList, order?.original.divisions),
    [order, props.controller?.data?.allDivisionList]
  );

  const handleSelectedItem: SelectedItemHandler = useCallback(
    async (value, key, data) => {
      setSelectedDoctor(data || null);
    },
    [props.searchedItemListWithKey]
  );

  const handleClickAdd = () => {
    if (!department || !order) {
      setFormErrors({ department: !department, order: !order });

      return;
    }

    setFormErrors(null);

    tableData.push({
      detail: "",
      duration: 0,
      frequency: 0,
      icd9_type: "SECONDARY",
      // icd9Name: "",
      divisions: order.original?.divisions || [],
      icd9Data: null,
      productName: order.text,
      rehabilitation: order.key,
      to_department_for_doctor: department,
      total_for_doctor: 0,
    });
    setTableData([...tableData]);
    setOrder(null);
    setDepartment(null);
  };

  const handleSave = () => {
    // Check icd9_type === "PRIMARY"
    const filteredIcd9Items = tableData.filter((item) => item.icd9Data);
    const isAllPrimaryIcd9 = filteredIcd9Items.every((item: any) => item.icd9_type === "SECONDARY");

    if (filteredIcd9Items.length > 0 && isAllPrimaryIcd9) {
      setFormErrors({ primary: true });

      return;
    }

    const isNoTotalInRows = tableData.some(
      (item) => !item.total_for_doctor && !item.canceled_reason
    );

    if (isNoTotalInRows) {
      setFormErrors({
        totals: tableData.flatMap((item, index) => (item.total_for_doctor ? [] : [index])),
      });

      return;
    }

    setFormErrors(null);

    let data: any = {
      id: props.rehabExamPlan?.id,
      btnAction: ACTION_SAVE,
      goal: props.rehabExamPlan?.goal,
      items: tableData,
      precaution: (props.rehabExamPlan?.precaution ?? [])
        .map((value) => rhbPrecautionOptions.find((r: any) => r.value === value)?.text as string)
        .filter(Boolean),
      problem: props.rehabExamPlan?.problem,
      
    };

    
    if (isDoctor) {
      data.order_by = props.currentDoctor?.id || null;
      data.order_receive_by = null;
    } else {
      data.order_by = selectedDoctor?.id || null;
      data.order_receive_by = props.django?.user?.id;
    }    

    props.onEvent({ message: "createUpdateRhbExamPlan", params: data });
  };

  const handleCloseModInfo = () => {
    setFormErrors(null);
  };

  const handleChangeTableData = (index: number, key: string, value: any) => {
    let data: Record<string, any>[] = [...tableData];

    if (key === "icd9_type" && value === "PRIMARY") {
      data = data.map((item) => ({ ...item, icd9_type: "SECONDARY" }));
    }

    if (key === "to_department_for_doctor") {
      data[index].to_department_for_doctor_changed = true;
    }

    if (key === "total_for_doctor") {
      data[index].total_for_doctor_changed = true;
    }

    data[index][key] = value;

    setTableData([...data]);
  };

  const handleDeleteDataIdx = (index: number) => {
    tableData.splice(index, 1);
    setTableData([...tableData]);
  };

  const handleOpenGallery = (rowIndex: number) => {
    const data = tableData[rowIndex];

    const images: RehabImageType[] = data.images ?? [];

    const defaultImage =
      props.selectedEncounter?.patient_gender_name === "Female"
        ? IMAGES.female_diagram
        : IMAGES.male_diagram;

    if (images.length === 0) {
      const diagram = {
        image: defaultImage,
        key: "Body Diagram",
        name: "Body Diagram",
        original: defaultImage,
      };

      setActivePanes([diagram]);
    } else {
      const formattedImage = formatRehabImage(images);

      setImageOverlay(formattedImage.imageOverlay);
      setActivePanes(formattedImage.activePanes);
    }

    setActiveIndex(0);

    setModalEditImage(rowIndex);
  };

  const formatImageData = async (image: Record<string, any>): Promise<RehabImageType[]> => {
    // ฟังก์ชันสำหรับแปลง URL เป็น File
    const convertUrlToFile = async (url: string): Promise<File> => {
      const response = await fetch(url);
      const blob = await response.blob();

      const mimeToExt: Record<string, string> = {
        "image/jpeg": "jpg",
        "image/jpg": "jpg",
        "image/png": "png",
      };

      const extension = mimeToExt[blob.type] || "png";

      return new File([blob], `temp.${extension}`, {
        lastModified: Date.now(),
        type: blob.type,
      });
    };

    const isUrl = (value: any): boolean => typeof value === "string";

    const convertToFile = async (fileOrUrl: File | string) => {
      if (!fileOrUrl) {
        return null;
      }

      return isUrl(fileOrUrl) ? convertUrlToFile(fileOrUrl as string) : fileOrUrl;
    };

    const convertFileToObjectURI = async (imageFile?: File) => {
      if (!imageFile) {
        return null;
      }

      return URL.createObjectURL(imageFile);
    };

    const renameFile = (file?: File, prefix?: string) => {
      if (!file) {
        return null;
      }

      const fileExtension = file.name.split(".").pop();
      const fileName = `${prefix}${uuidv4()}.${fileExtension}`;

      return new File([file], fileName, {
        lastModified: file.lastModified,
        type: file.type,
      });
    };

    const imagesPromise = Object.entries(image).map(async ([key, value]) => {
      const imageFile = await convertToFile(value.image_file);
      const originalFile = await convertToFile(value.original);

      if (!originalFile && !imageFile) {
        return [];
      }

      const imageBlob = await convertFileToObjectURI(imageFile as File);
      const originalBlob = await convertFileToObjectURI(originalFile as File);

      return [
        {
          id: value.id,
          file: imageBlob,
          file_name: key,
          image: renameFile(imageFile as File, "overlay-"),
          is_overlay: true,
        },
        {
          id: value.original_id,
          file: originalBlob,
          file_name: key,
          image: renameFile(originalFile as File, "original-"),
          is_original: true,
        },
      ];
    });

    const imageResults = await Promise.all(imagesPromise);

    return imageResults.flat().filter((imageItem) => Boolean(imageItem.image)) as RehabImageType[];
  };

  const handleEditedImageOverlay = async (image: Record<string, any>) => {
    if (typeof modalEditImage !== "number") {
      return;
    }

    const imageEntries = activePanes.map((item) => {
      const imageFile = image[item.key]?.image_file;
      const original: string = item.original || item.image;
      const overlay: File | string = imageFile || item.overlay || item.image;

      return [
        item.name as string,
        {
          id: item.overlay_id,
          image_file: String(overlay).startsWith("/static/") ? null : overlay,
          original,
          original_id: item.id,
        },
      ];
    });

    const imageMap = Object.fromEntries(imageEntries);

    const images = await formatImageData(imageMap);

    tableData[modalEditImage].images = images;

    const data = tableData[modalEditImage];

    if (data.id) {
      props.onEvent({
        message: "createUpdateRhbImage",
        params: {
          formattedItems: [data],
          planItems: [data],
        },
      });
    }

    setTableData([...tableData]);
  };

  const handleUpLoadPhoto = () => {
    setOpenModalUploadFile(true);
  };

  const handleSaveNewImage = (image: string, name?: string) => {
    const panes = [...activePanes];
    const defaultName = `DIAGRAM-${panes.length}`;

    let tabName = (name || defaultName).replace(/\.[^.]*$/, "");

    const isDup = panes.some((pane) => pane.key === tabName);

    if (isDup) {
      tabName = `${tabName}-1`;
    }

    panes.push({
      editable: true,
      image,
      key: tabName,
      name: tabName,
      original: image,
      removable: true,
    });

    setActivePanes(panes);
    setActiveIndex(panes.length - 1);
  };

  const handleTakePhoto = () => {
    setOpenWebCam(true);
  };

  const handleDeleteActivePane = (index: number) => {
    activePanes.splice(index, 1);
    setActiveIndex(index - 1);
    setActivePanes([...activePanes]);
  };

  const handleRenameActivePane = (index: number, value: string) => {
    activePanes[index].name = value;
    setActivePanes([...activePanes]);
  };

  const handleCloseEditImage = () => {
    setModalEditImage(null);
    setActivePanes([]);
    setImageOverlay([]);
  };

  const handleChangeDepartment = (e: SyntheticEvent, data: DropdownProps) => {
    setDepartment(Number(data.value));

    if (formErrors) {
      setFormErrors({ ...formErrors, department: !data.value });
    }

    console.log("setDepartment data:", data);
  };

  const handleSelectOrder: SelectedItemHandler = (value, key, data) => {
    if (value) {
      const divisionId = data?.divisions[0] || "";

      setOrder({ key, original: data, text: data?.name, value });
      setDepartment(divisionId);

      if (formErrors) {
        setFormErrors({ ...formErrors, department: !divisionId, order: false });
      }

      return;
    }

    setOrder(null);
    setDepartment(null);
  };

  console.log("CardRehabExamination props:", props);

  return (
    <>
      <SnackMessage
        error={props.errorMessage?.[ACTION_SAVE]}
        success={null}
        onClose={() => {
          props.setProp(`errorMessage.${ACTION_SAVE}`, null);
        }}
      />

      <CardRehabExaminationUX
        department={department}
        // disabledAddBtn={!department || !order}
        disableSave={!tableData.some((item) => item.icd9_type === "PRIMARY")}
        formErrors={formErrors}
        goal={props.rehabExamPlan?.goal}
        precaution={props.rehabExamPlan?.precaution}
        problem={props.rehabExamPlan?.problem}
        languageUX={props.languageUX}
        departmentOptions={rehabDivisions}
        precautionOptions={rhbPrecautionOptions}
        // order={order}
        // onChangeOrder={(event: SyntheticEvent, data: TextAreaProps) => setOrder(data.value as string)}
        onChangeDepartment={handleChangeDepartment}
        onChangeGoal={(event: SyntheticEvent, data: TextAreaProps) => {
          props.setProp("rehabExamPlan.goal", data.value as string);
        }}
        onChangePrecaution={(event: SyntheticEvent, data: TextAreaProps) => {
          props.setProp("rehabExamPlan", {
            ...props.rehabExamPlan,
            precaution: data.value,
          });
        }}
        onChangeProblem={(event: SyntheticEvent, data: TextAreaProps) => {
          props.setProp("rehabExamPlan.problem", data.value as string);
        }}
        onClickAdd={handleClickAdd}
        doctorSearchBox={
          <div style={{ display: !isDoctor ? "flex" : "none" }}>
            <label 
              style={{ marginTop: "10px", paddingRight: "10px", fontWeight: "bold" }}
            >
              แพทย์ผู้สั่ง
            </label>
            <div  style={{ width: "80%" }}>
              <SearchBoxDropdown
                onEvent={props.onEvent}
                // config
                type="Doctor"
                id="RD"
                style={{ width: "100%" }}
                fluid={true}
                useWithKey={true}
                icon="search"
                limit={20}
                upward={true}
                inline
                // Select
                searchedItemListWithKey={props.searchedItemListWithKey}
                selectedItem={selectedDoctor?.id || null}
                setSelectedItem={handleSelectedItem}
                defaultText={selectedDoctor?.name_code}
              />
            </div>
          </div>
        }
        buttonSave={
          <ButtonLoadCheck
            setProp={props.setProp}
            color="green"
            disabled={tableData.length === 0 || !(isDoctor || selectedDoctor?.id)}
            paramKey={ACTION_SAVE}
            size="medium"
            title="SAVE"
            buttonLoadCheck={props.buttonLoadCheck?.[ACTION_SAVE]}
            onClick={handleSave}
          />        
        }
        searchOrder={
          <SearchBoxWithKey
            // callback
            onEvent={props.onEvent}
            id="RehabTreatment_1"
            error={formErrors?.order}
            selectedItem={order?.value}
            type="RehabTreatment"
            searchedItemListWithKey={props.searchedItemListWithKey}
            setSelected={handleSelectOrder}
          />
        }
        table={
          <TableTreatmentPlan
            onEvent={props.onEvent}
            // icd9Table={props.rehabExamPlan?.icd9Table}
            allDivisionList={props.controller?.data?.allDivisionList}
            data={tableData}
            deleteDataIdx={handleDeleteDataIdx}
            totalsError={formErrors?.totals}
            searchedItemListWithKey={props.searchedItemListWithKey}
            onChangeTableData={handleChangeTableData}
            onOpenGallery={handleOpenGallery}
          />
        }
      />

      <ModInfo
        open={formErrors?.primary}
        titleColor="red"
        onApprove={handleCloseModInfo}
        onClose={handleCloseModInfo}
      >
        <div style={{ fontWeight: "bold", padding: "1rem 0" }}>
          Please specify the primary procedure
        </div>
      </ModInfo>

      <ModalEditImage
        activeIndex={activeIndex}
        activePanes={activePanes}
        cancelImage={handleCloseEditImage}
        editedImageOverlay={handleEditedImageOverlay}
        hiddenTab={false}
        hideBabyTooth={false}
        imageOverlay={imageOverlay}
        openEditModal={typeof modalEditImage === "number"}
        onCloseModal={handleCloseEditImage}
        onDeleteTab={handleDeleteActivePane}
        onRenameTab={handleRenameActivePane}
        tabAddControl={
          <>
            <Button style={{ transform: "scale(0.8)" }} color="blue" onClick={handleTakePhoto}>
              <Icon name="camera" />
              Take Photo
            </Button>
            <Button
              style={{ marginLeft: "-1rem", transform: "scale(0.8)" }}
              color="blue"
              onClick={handleUpLoadPhoto}
            >
              <Icon name="upload" />
              Upload
            </Button>
          </>
        }
      />

      <ModalUploadFile
        openUploadFileModal={openModalUploadFile}
        onCloseModal={setOpenModalUploadFile}
        onUploadFile={handleSaveNewImage}
      />

      <WebcamComponent
        open={openWebCam}
        setNewImage={handleSaveNewImage}
        onClose={() => {
          setOpenWebCam(false);
        }}
      />
    </>
  );
};

CardRehabExamination.displayName = "CardRehabExamination";

export default React.memo(CardRehabExamination);
